import type { ContestSettings, SlateLabelEnum } from '@betterpool/api-types/contests-service';
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import { Modal, createStyles, Title, Accordion, Flex } from '@mantine/core';
import { format } from 'date-fns';
import type { Dispatch, SetStateAction } from 'react';
import { Fragment } from 'react';

import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';
import dt from '~/testing';

import { SectionGridLabel, SectionGridValue } from '../components';

import type { SegmentTab } from './useSegmentedTabs';
import ScheduleTabsControl from './ScheduleTabsControl';
import useGetScheduleTitle from './useGetScheduleTitle';
import useGetSegmentedData from './useSegmentedData';

const useStyles = createStyles((theme) => ({
  item: {
    borderBottomColor: theme.colors.gray[2],
  },
  chevron: {
    marginRight: theme.other.spacing._4,
    marginLeft: 0,
    [theme.fn.smallerThan('md')]: {
      margin: 0,
    },
  },
}));

type ScheduleModalProps = {
  slates: ContestSlate[];
  isOpen: boolean;
  isSidePanel?: boolean;
  onClose: VoidFunction;
  segments?: ContestSettings['segments'];
  slateLabel?: SlateLabelEnum;
  tabs: {
    label: string;
    value: SegmentTab;
  }[];
  activeTab: SegmentTab;
  setActiveTab: Dispatch<SetStateAction<SegmentTab>>;
  groupStageSlateId?: string;
};

function ScheduleModal({
  slates = [],
  isSidePanel,
  isOpen,
  onClose,
  segments,
  slateLabel,
  tabs,
  activeTab,
  setActiveTab,
  groupStageSlateId,
}: ScheduleModalProps) {
  const { classes } = useStyles();
  const theme = useTheme();

  const segmentTitle = useGetScheduleTitle({
    slates,
    slateLabel,
    segmentType: segments?.segmentType,
  });

  const items = useGetSegmentedData({
    slates,
    activeTab,
    segments,
    slateLabel,
    groupStageSlateId,
  });

  return (
    <Modal
      onClose={onClose}
      opened={isOpen}
      title={<Title size={theme.fontSizes.lg}>{segmentTitle}</Title>}
      size="lg"
      radius="md"
      fullScreen
    >
      <ScheduleTabsControl
        segments={segments}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
      />
      {items.map((data) => (
        <Fragment key={data.value}>
          <Accordion
            classNames={{ chevron: classes.chevron }}
            chevronSize={data.isExpandable ? 16 : 0}
          >
            <Accordion.Item className={classes.item} value={data.value} key={data.label}>
              <Accordion.Control p={0} disabled={!data.isExpandable}>
                <Flex
                  justify="space-between"
                  align="center"
                  p={theme.other.spacing._16}
                  data-test-id={dt.contestDetail.section.prizes.row}
                >
                  <SectionGridLabel isSidePanel={isSidePanel}>{data.label}</SectionGridLabel>
                  <SectionGridValue isSidePanel={isSidePanel}>
                    Starts {data.startDate}
                  </SectionGridValue>
                </Flex>
              </Accordion.Control>
              {data.isExpandable &&
                data.data.map(({ name, startDate }) => (
                  <Accordion.Panel key={name}>
                    <Flex justify="space-between">
                      <SectionGridLabel isSidePanel={isSidePanel}>{name}</SectionGridLabel>
                      <SectionGridValue isSidePanel={isSidePanel}>
                        {format(new Date(startDate), "MM/dd '@' h:mm a")}
                      </SectionGridValue>
                    </Flex>
                  </Accordion.Panel>
                ))}
            </Accordion.Item>
          </Accordion>
        </Fragment>
      ))}
    </Modal>
  );
}

export default ScheduleModal;
